import React from "react";
import { Badge, Box, Button, Flex, Group, Loader } from "@mantine/core";
import { format, formatDistanceToNowStrict, parseISO, addMinutes } from "date-fns";

import Text from "components/Text/Text";
import { ReactComponent as TriangleIcon } from "assets/triangle.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { ReactComponent as TagIcon } from 'assets/tag.svg';
import { ReactComponent as AddTagIcon } from 'assets/tag-add.svg';
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { ReactComponent as AlarmIcon } from "assets/alarm.svg";
// COMMENTED OUT: Email invite functionality - needs proper implementation for Gmail compatibility
// import { ReactComponent as MailIcon } from "assets/mail.svg";
import { ReactComponent as CheckIcon } from "assets/check.svg";
import styles from './Summary.module.scss';
import { Tab } from "Modules/PractitionerBaseballCard";
import useExternalApiService from '../../../../helpers/ExternalApiService';
import { formatMoney, formatMoneyWithZero } from "helpers";
import { store } from "stores/Store";
import TagsMenu from "./TagsMenu";
import MeetingScheduleForm from "components/Calendar/MeetingScheduleForm";
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import CalendarConfirmationModal from "components/Calendar/CalendarConfirmationModal";
// COMMENTED OUT: Email invite functionality - needs proper implementation for Gmail compatibility
// import EmailComposer from "components/Calendar/EmailComposer";

interface ITag {
  id: number;
  name: string;
  selected: boolean;
}

interface ISummary {
  isLoadingFollowUpDates: boolean;
  navigate: (path: Tab) => void;
  onOpenFollowUpMenu: () => void;
  onClickEditFollowUpDate: () => void;
  onCompleteFollowUp: () => Promise<void>;
}

const Summary = ({ isLoadingFollowUpDates, onOpenFollowUpMenu, onClickEditFollowUpDate, onCompleteFollowUp}: ISummary) => {

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const {postData, getData, patchData} = useExternalApiService(apiUrl);

  const [keystats, setKeystats] = React.useState<any>(null);
  const [isTagsMenuOpen, setIsTagsMenuOpen] = React.useState<boolean>(false);
  const [isSubmittingCompleteFollowUp, setIsSubmittingCompleteFollowUp] = React.useState<boolean>(false);
  const [isCalendarAdded, setIsCalendarAdded] = React.useState<boolean>(false);
  const [upcomingMeetings, setUpcomingMeetings] = React.useState<any[]>([]);
  const [isLoadingUpcomingMeetings, setIsLoadingUpcomingMeetings] = React.useState<boolean>(false);
  // COMMENTED OUT: Email invite functionality - needs proper implementation for Gmail compatibility
  // const [isInviteSent, setIsInviteSent] = React.useState<boolean>(false);
  const [showCalendarModal, setShowCalendarModal] = React.useState<boolean>(false);
  // COMMENTED OUT: Email invite functionality - needs proper implementation for Gmail compatibility
  // const [showEmailComposer, setShowEmailComposer] = React.useState<boolean>(false);
  const [showCalendarProviderSelection, setShowCalendarProviderSelection] = React.useState<boolean>(false);
  // COMMENTED OUT: Email invite functionality - needs proper implementation for Gmail compatibility
  // const [isSendingEmail, setIsSendingEmail] = React.useState<boolean>(false);
  const [calendarProviderClicked, setCalendarProviderClicked] = React.useState<string | null>(null);

  // Calendar meeting details
  const [meetingTime, setMeetingTime] = React.useState<string>('10:00');
  const [meetingDuration, setMeetingDuration] = React.useState<string>('30 min');
  const [meetingLocation, setMeetingLocation] = React.useState<string>('');
  const [meetingAgenda, setMeetingAgenda] = React.useState<string>('');

  // 2) Pull out the practitioner properties
  const practitionerName = store.currentPractitioner?.primary_name;
  const dataSource = store.currentPractitioner?.data_source;
  const status = store.currentPractitioner?.status;
  const tags = store.currentPractitioner?.tags ?? [];
  const nextFollowUpDate = store.currentPractitionerNextFollowUpDate;

  // Helper function to convert 12-hour time to 24-hour format
  const convert12HourTo24Hour = (time12h: string): string => {
    if (!time12h) return '';

    // Check if already in 24-hour format (no AM/PM)
    if (!time12h.includes('AM') && !time12h.includes('PM')) {
      return time12h;
    }

    const [time, period] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (period === 'PM' && hours !== '12') {
      hours = String(parseInt(hours, 10) + 12);
    } else if (period === 'AM' && hours === '12') {
      hours = '00';
    }

    return `${hours.padStart(2, '0')}:${minutes}`;
  };

  // Helper function to calculate end time from start time and duration
  const calculateEndTime = (startTime: string, duration: string): string => {
    if (!startTime || !nextFollowUpDate) return '';

    const time24h = convert12HourTo24Hour(startTime);
    const dateStr = format(parseISO(nextFollowUpDate), 'yyyy-MM-dd');
    const durationMinutes = parseInt(duration.replace(/\D/g, '')) || 30;

    const endDateTime = addMinutes(
      parseISO(`${dateStr}T${time24h}:00`),
      durationMinutes
    );

    return format(endDateTime, 'HH:mm');
  };

  const lastCheckInDate = store.currentPractitionerLastCheckinDate && store.currentPractitionerLastCheckinDate.includes('T') ? `${store.currentPractitionerLastCheckinDate}Z` : store.currentPractitioner?.last_checkin_date;

  const selectedTags = tags.filter((tag: { selected: boolean; }) => tag.selected);

  const shouldShowWarning =
    dataSource === "PRACTITIONER_ACCOUNT_VIEW_NM" ||
    status === "LEAD-INCOMPLETE" ||
    status === "LEAD-READY2SUBMIT";

  // Auto-populate location from practitioner address when modal opens
  React.useEffect(() => {
    if (showCalendarModal && store.currentPractitioner && !meetingLocation) {
      // Check if addresses array exists and has entries
      if (store.currentPractitioner.addresses && store.currentPractitioner.addresses.length > 0) {
        const primaryAddress = store.currentPractitioner.addresses[0];

        // Use address1 if it's already formatted with commas (complete address)
        if (primaryAddress.address1 && primaryAddress.address1.includes(',')) {
          setMeetingLocation(primaryAddress.address1);
        } else {
          // Build from components
          const locationParts = [
            primaryAddress.address1 || primaryAddress.address,
            primaryAddress.city,
            primaryAddress.state,
            primaryAddress.zipcode || primaryAddress.zip
          ].filter(Boolean);

          if (locationParts.length > 0) {
            setMeetingLocation(locationParts.join(', '));
          }
        }
      }
    }
  }, [showCalendarModal]);

  // Load calendar state from database when component mounts or follow-up date changes
  React.useEffect(() => {
    if (!store.currentPractitioner?.id || !nextFollowUpDate) {
      setIsCalendarAdded(false);
      return;
    }

    // Fetch calendar state from database for cross-device persistence
    const loadCalendarState = async () => {
      try {
        const response = await postData('/api/e/call_get_calendar_confirmation', {
          practitioner_id: store.currentPractitioner.id,
          follow_up_date: nextFollowUpDate
        });

        if (response && response.success) {
          setIsCalendarAdded(response.calendar_added || false);
        } else {
          setIsCalendarAdded(false);
        }
      } catch (error) {
        console.error('Error loading calendar state:', error);
        setIsCalendarAdded(false);
      }
    };

    loadCalendarState();
    setCalendarProviderClicked(null);
  }, [nextFollowUpDate, store.currentPractitioner?.id, postData]);

  // Reset location when modal closes
  React.useEffect(() => {
    if (!showCalendarModal) {
      setMeetingLocation('');
    }
  }, [showCalendarModal]);

  // Load upcoming meetings
  React.useEffect(() => {
    const loadUpcomingMeetings = async () => {
      if (!store.currentPractitioner?.id) return;

      setIsLoadingUpcomingMeetings(true);
      try {
        const response = await postData('/api/e/call_get_upcoming_meetings', {
          practitioner_id: store.currentPractitioner.id
        });

        if (response?.success && response?.meetings) {
          // Deduplicate meetings by datetime + type (fixes duplicate calendar provider clicks)
          const uniqueMeetingsMap = new Map<string, any>();

          response.meetings.forEach((meeting: any) => {
            // Create unique key: datetime + type
            const key = `${meeting.follow_up_date}-${meeting.type}`;

            // Only keep first occurrence (oldest ID = first calendar confirmation)
            if (!uniqueMeetingsMap.has(key)) {
              uniqueMeetingsMap.set(key, meeting);
            }
          });

          const deduplicatedMeetings = Array.from(uniqueMeetingsMap.values());
          setUpcomingMeetings(deduplicatedMeetings);
        }
      } catch (error) {
        console.error('Failed to load upcoming meetings:', error);
      } finally {
        setIsLoadingUpcomingMeetings(false);
      }
    };

    loadUpcomingMeetings();
  }, [store.currentPractitioner?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const closeTagsMenu = () => {
    setIsTagsMenuOpen(false);
  }

  const updatePractitionerCache = React.useCallback((updatedTags: ITag[]) => {
    if (store.impersonatedUser) {
      const currentUser = store.impersonatedUser; // Store in local variable for TypeScript
      const userCache = store.practitionersCache[currentUser];
      if (userCache) {
        Object.keys(userCache).forEach(pageKey => {
          const page = parseInt(pageKey);
          const cachedPractitioners = store.getPractitionersCache(currentUser, page);
          if (cachedPractitioners) {
            const updatedCache = cachedPractitioners.map((practitioner: any) =>
              practitioner.id === store.currentPractitioner?.id
                ? {...practitioner, tags: updatedTags}
                : practitioner
            );
            store.setPractitionersCache(currentUser, page, updatedCache);
          }
        });
      }
    }
  }, []);

  const refetchTags = React.useCallback(async () => {
    const response = await getData(`/api/practitioner/${store.currentPractitioner?.id}/tags`);
    store.setCurrentPractitioner({...store.currentPractitioner, tags: response.tags});
  }, [getData]);

  const handleSelectTag = async (tagId: number) => {
    const optimisticTags = tags.map((tag: ITag) => 
      tag.id === tagId ? {...tag, selected: true} : tag
    );
    store.setCurrentPractitioner({...store.currentPractitioner, tags: optimisticTags});
    updatePractitionerCache(optimisticTags);
    
    try {
      await postData(`/api/practitioner/${store.currentPractitioner?.id}/tags/assign`, {tag_ids: [tagId]});
      refetchTags();
    } catch (error) {
      const revertedTags = tags.map((tag: ITag) => 
        tag.id === tagId ? {...tag, selected: false} : tag
      );
      store.setCurrentPractitioner({...store.currentPractitioner, tags: revertedTags});
      updatePractitionerCache(revertedTags);
      console.error('Failed to assign tag:', error);
    }
  };

  const handleUnselectTag = async (tagId: number) => {
    const optimisticTags = tags.map((tag: ITag) => 
      tag.id === tagId ? {...tag, selected: false} : tag
    );
    store.setCurrentPractitioner({...store.currentPractitioner, tags: optimisticTags});
    updatePractitionerCache(optimisticTags);
    
    try {
      await postData(`/api/practitioner/${store.currentPractitioner?.id}/tags/unassign`, {tag_ids: [tagId]});
      refetchTags();
    } catch (error) {
      const revertedTags = tags.map((tag: ITag) => 
        tag.id === tagId ? {...tag, selected: true} : tag
      );
      store.setCurrentPractitioner({...store.currentPractitioner, tags: revertedTags});
      updatePractitionerCache(revertedTags);
      console.error('Failed to unassign tag:', error);
    }
  }

  const handleTagCreation = async (tagName: string) => {
    await postData('/api/practitioner/tags/create', {tag: tagName});

    refetchTags();
  };

  const handleUnassignTag = (tagId: number) => async () => {
    const optimisticTags = tags.map((tag: ITag) => 
      tag.id === tagId ? {...tag, selected: false} : tag
    );
    store.setCurrentPractitioner({...store.currentPractitioner, tags: optimisticTags});
    updatePractitionerCache(optimisticTags);
    
    try {
      await postData(`/api/practitioner/${store.currentPractitioner?.id}/tags/unassign`, {tag_ids: [tagId]});
      refetchTags();
    } catch (error) {
      const revertedTags = tags.map((tag: ITag) => 
        tag.id === tagId ? {...tag, selected: true} : tag
      );
      store.setCurrentPractitioner({...store.currentPractitioner, tags: revertedTags});
      updatePractitionerCache(revertedTags);
      console.error('Failed to unassign tag:', error);
    }
  };

  const handleTagEdits = async (updatedTags: {id: number, name: string}[], deletedTags: number[]) => {
    if (updatedTags.length > 0) {
      await patchData('/api/practitioner/tags/update', {tags: updatedTags});
    }

    if (deletedTags.length > 0) {
      await postData('/api/practitioner/tags/delete', {tag_ids: deletedTags});

      if (store.impersonatedUser) {
        const currentUser = store.impersonatedUser; // Store in local variable for TypeScript
        const userCache = store.practitionersCache[currentUser];
        if (userCache) {
          Object.keys(userCache).forEach(pageKey => {
            const page = parseInt(pageKey);
            const cachedPractitioners = store.getPractitionersCache(currentUser, page);
            if (cachedPractitioners) {
              const updatedCache = cachedPractitioners.map((practitioner: any) => {
                if (practitioner.tags && practitioner.tags.length > 0) {
                  const filteredTags = practitioner.tags.filter((tag: ITag) => !deletedTags.includes(tag.id));
                  return {...practitioner, tags: filteredTags};
                }
                return practitioner;
              });
              store.setPractitionersCache(currentUser, page, updatedCache);
            }
          });
        }
      }
    }

    refetchTags();
  };

  const handleSubmitCompleteFollowUp = async () => {
    try {
      setIsSubmittingCompleteFollowUp(true);
      await onCompleteFollowUp();
    } finally {
      setIsSubmittingCompleteFollowUp(false);
    }
  }

  React.useEffect(() => {
    if (!practitionerName) return;

    const getKeystats = async () => {
      const response = await postData('/api/e/call_winback_keystats_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? ''});
      console.log(response.payload);
      setKeystats(response.payload);
    }
    getKeystats();
    refetchTags();
  }, [practitionerName]);

  // ── 2) Check warning conditions (data_source or status) ──────────────────────
  //
  // The three cases where we want to show a “warning” screen instead of Key Stats:
  //  • data_source === "PRACTITIONER_ACCOUNT_VIEW_NM"
  //  • status === "LEAD-INCOMPLETE"
  //  • status === "LEAD-READY2SUBMIT"
  //

  return(
    <>
     {/*
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>NEXT STEPS</p>
      </div>
        <div className={styles.horizontalScrollItemWrapper}>
          <Button type="outline">FOLLOW UP</Button>
        </div>
        <div className={styles.horizontalScrollItemWrapper}>
          <Button type="outline">xxxxxxx</Button>
        </div>
        <div className={styles.horizontalScrollItemWrapper}>
          <Button type="outline">xxxxxxx</Button>
        </div>
        
      </div>
      */}
      {/* <div className={styles.sectionWrapper} onClick={() => navigate(Tab.OPPORTUNITIES)}>
        <p className={styles.headerText}>OPPORTUNITIES<ExpandIcon /></p>
      </div>
      <div className={styles.rowWrapper}>
        <div className={styles.columnWrapper}>
          <p className={styles.HugeText}>$56K</p>
          <Text>Opportunity by March 20</Text>
        </div>
        <div className={styles.columnWrapper}>
          <Text bold>1. GI Revive: 56 units</Text>
          <Text bold>2. CoQnol: 30 units</Text>
          <Text bold>3. Spotlight: 30 units</Text>
        </div>
      </div> */}
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>NEXT STEPS</p>
        <Box px={10}>
          <p style={{ fontSize: 14, margin: 0 }}>
            <strong>Follow-Up</strong>
            {isLoadingFollowUpDates ? (
              <Loader size="sm" ml={10} />
            ) : nextFollowUpDate ? (
              <>
                <span> on </span>
                <Button
                  variant="white"
                  onClick={onClickEditFollowUpDate}
                  classNames={{ root: styles.editFollowUpDateButton }}
                >
                  {format(parseISO(nextFollowUpDate), 'MMM. dd, yyyy')}
                </Button>
              </>
            ) : (
              <span className={styles.followUpNotSet}> not set</span>
            )}
          </p>

          {nextFollowUpDate ? (
            <Group my="md" grow px={0}>
              <Button
                type="button"
                onClick={handleSubmitCompleteFollowUp}
                bg="#58E487"
                size="xs"
                disabled={isSubmittingCompleteFollowUp}
                loading={isSubmittingCompleteFollowUp}
                style={{ flex: 1 }}
              >
                Complete Follow-Up
              </Button>
              <Button
                type="button"
                onClick={() => setShowCalendarModal(true)}
                bg={isCalendarAdded ? '#58E487' : '#FF9D00'}
                size="xs"
                leftSection={isCalendarAdded ? <CheckIcon width={15} style={{ stroke: "white" }} /> : <AlarmIcon width={15} style={{ fill: "white" }} />}
                style={{ flex: 1 }}
              >
                {isCalendarAdded ? 'Added to Calendar' : 'Add to Calendar'}
              </Button>
            </Group>
          ) : (
            <Group my="md" grow px={0}>
              <Button
                type="button"
                onClick={onOpenFollowUpMenu}
                bg="#E64B37"
                size="xs"
                style={{ flex: 1 }}
              >
                Set Follow-Up
              </Button>
            </Group>
          )}

          <p style={{ fontSize: 14 }}>
            <strong>Last Check-In</strong>
            {isLoadingFollowUpDates ? (
              <Loader size="sm" ml={10} />
            ) : lastCheckInDate ? (
              <span> on {format(parseISO(lastCheckInDate), 'MMM. dd yyyy')} ({formatDistanceToNowStrict(parseISO(lastCheckInDate), {addSuffix: true, unit: 'day', roundingMethod: 'floor'})})</span>
            ) : (
              <span> - No recorded check-in</span>
            )}
          </p>

          <p style={{ fontSize: 14, marginTop: 16 }}>
            <strong>Upcoming Meetings (added to calendar only)</strong>
            {isLoadingUpcomingMeetings ? (
              <Loader size="sm" ml={10} />
            ) : upcomingMeetings.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                {upcomingMeetings.map((meeting) => (
                  <div key={meeting.id} style={{
                    fontSize: 13,
                    padding: '4px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <span style={{ fontWeight: 500 }}>
                      {format(parseISO(meeting.follow_up_date), 'MMM dd, yyyy h:mm a')}
                    </span>
                    <span style={{ color: '#666', marginLeft: 8 }}>
                      - {meeting.type === 'Route' || meeting.type.startsWith('Route:') ? 'Route Visit' : meeting.type === 'Calendar Confirmation' ? 'Follow Up' : meeting.type}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ fontSize: 13, color: '#999' }}> - No upcoming meetings</span>
            )}
          </p>
        </Box>
      </div>
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>TAGS <TagIcon /></p>
        <Flex
          gap={10}
          p={10}
          justify="center"
          align="center"
        >
          {selectedTags.length === 0 ? (
            <p className={styles.tagsEmpty}>
              Tags allow you to create <strong>custom labels</strong> to be used in account search
            </p>
          ) : (
            <Flex flex={1} gap={10} wrap="wrap">
              {selectedTags.map((tag: { id: number; name: string; }) => (
                <Badge
                  key={tag.id}
                  classNames={{ root: styles.tagBadge }}
                  rightSection={(
                    <button
                      type="button"
                      className={styles.unassignTagButton}
                      onClick={handleUnassignTag(tag.id)}
                    >
                      <CloseIcon width={16} />
                    </button>
                  )}
                >
                  {tag.name}
                </Badge>
              ))}
            </Flex>
          )}
          <TagsMenu
            isOpen={isTagsMenuOpen}
            tags={tags}
            triggerButton={(
              <button
                type="button"
                className={styles.addTagButton}
                onClick={() => setIsTagsMenuOpen(true)}
              >
                <AddTagIcon />
              </button>
            )}
            onSubmitTagSelect={handleSelectTag}
            onSubmitTagUnselect={handleUnselectTag}
            onSubmitTagCreation={handleTagCreation}
            onSubmitTagEdit={handleTagEdits}
            onClose={closeTagsMenu}
          />
        </Flex>
      </div>
      {shouldShowWarning ? (
        <div className={styles["summary-warning-container"]}>
          <p className={styles["summary-warning-text"]}>
            To view sales summary, this practitioner's baseball card needs to be matched in Netsuite to pull sales data.
          </p>
        </div>
      ) : (
        <div className={styles.sectionWrapper}>
          <p className={styles.headerText}>KEY STATS{/*<ExpandIcon />*/}</p>
          <div className={styles.twoColumnWrapper}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
              {/*<p style={{ color: keystats?.mtd_diff < 0 ? '#E64B37' : '#00DC49CC', fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>{formatMoneyWithZero(keystats?.mtd_sales)}</p>*/}
              <p style={{fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>{formatMoneyWithZero(keystats?.mtd_sales)}</p>
              {/* {
                keystats?.mtd_diff < 0 ? (
                  <TriangleIcon width="20px" height="20px" fill="#E64B37" />
                ) : (
                  <TriangleIcon width="20px" height="20px" fill="#00DC49CC" style={{ transform: 'rotate(180deg)' }} />
                )
              } */}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
              {/*<p style={{ color: keystats?.ytd_diff < 0 ? '#E64B37' : '#00DC49CC', fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>{formatMoneyWithZero(keystats?.ytd_sales)}</p>*/}
              <p style={{ fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>{formatMoneyWithZero(keystats?.ytd_sales)}</p>
              {/* {
                keystats?.ytd_diff < 0 ? (
                  <TriangleIcon width="20px" height="20px" fill="#E64B37" />
                ) : (
                  <TriangleIcon width="20px" height="20px" fill="#00DC49CC" style={{ transform: 'rotate(180deg)' }} />
                )
              } */}

            </div>
            <Text textAlign="center" bold>MTD Sales</Text>
            <Text textAlign="center" bold>YTD Sales</Text>
          </div>
        </div>
      )}
      {/* <div className={styles.twoColumnWrapper}>
        <p className={styles.ordersText}>15</p>
        <p className={styles.HugeText}>XXX</p>
        <Text textAlign="center" bold>Open Orders</Text>
        <Text textAlign="center" bold>YYY Metric</Text>
      </div> */}

      {/* Calendar Modal */}
      {showCalendarModal && nextFollowUpDate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {!showCalendarProviderSelection ? (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
                  Add Follow-Up to Calendar
                </h3>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                  Follow-up date: {format(parseISO(nextFollowUpDate), 'MMM. dd, yyyy')}
                </p>

                <MeetingScheduleForm
                  startTime={meetingTime}
                  onStartTimeChange={setMeetingTime}
                  duration={meetingDuration}
                  onDurationChange={setMeetingDuration}
                  location={meetingLocation}
                  onLocationChange={setMeetingLocation}
                  agenda={meetingAgenda}
                  onAgendaChange={setMeetingAgenda}
                  showCalendarOptions={true}
                  addToCalendar={true}
                  hideLocation={false}
                  hideCheckbox={true}
                />

                <Flex gap={10} mt={20}>
                  <Button
                    type="button"
                    fullWidth
                    onClick={() => {
                      setShowCalendarModal(false);
                      setShowCalendarProviderSelection(false);
                    }}
                    styles={{
                      root: {
                        backgroundColor: '#E64B3780',
                        color: '#E64B37',
                        fontSize: '11px',
                        fontWeight: 400,
                      }
                    }}
                  >
                    CANCEL
                  </Button>
                  <Button
                    type="button"
                    fullWidth
                    onClick={() => {
                      if (!meetingTime) {
                        alert('Please select a start time');
                        return;
                      }
                      // Validate that nextFollowUpDate is a valid date
                      try {
                        const testDate = parseISO(nextFollowUpDate);
                        if (isNaN(testDate.getTime())) {
                          alert('Invalid follow-up date. Please set a valid date first.');
                          return;
                        }
                      } catch (error) {
                        alert('Invalid follow-up date. Please set a valid date first.');
                        return;
                      }

                      // Show calendar provider selection modal
                      setShowCalendarProviderSelection(true);
                    }}
                    styles={{
                      root: {
                        backgroundColor: '#58E487',
                        fontSize: '11px',
                        fontWeight: 400,
                      }
                    }}
                  >
                    SAVE
                  </Button>
                </Flex>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Calendar Confirmation Modal */}
      <CalendarConfirmationModal
        isOpen={showCalendarProviderSelection}
        meetingDetails={
          nextFollowUpDate && meetingTime
            ? {
                name: `Follow-up: ${store.currentPractitioner?.primary_name || 'Practitioner'}`,
                startDate: format(parseISO(nextFollowUpDate), 'yyyy-MM-dd'),
                startTime: convert12HourTo24Hour(meetingTime),
                endTime: calculateEndTime(meetingTime, meetingDuration),
                location: meetingLocation,
                description: meetingAgenda,
                timeZone: 'America/New_York'
              }
            : undefined
        }
        onConfirm={async () => {
          setIsCalendarAdded(true);

          // Persist calendar state to database for cross-device persistence
          if (store.currentPractitioner?.id && nextFollowUpDate) {
            try {
              await postData('/api/e/call_save_calendar_confirmation', {
                practitioner_id: store.currentPractitioner.id,
                follow_up_date: nextFollowUpDate,
                meeting_source: 'Follow Up'  // Mark as Follow Up meeting
              });
            } catch (error) {
              console.error('Error saving calendar state:', error);
            }
          }
        }}
        onClose={() => {
          // Close modal and return to Summary tab
          setShowCalendarModal(false);
          setShowCalendarProviderSelection(false);
          setCalendarProviderClicked(null);
        }}
      />
    </>
  )
}

export default Summary;